import { useState, useEffect } from 'react';
import { whatsappApi } from '../../lib/api';
import {
  MessageCircle, Send, CheckCircle2, XCircle, Loader2, Eye, EyeOff,
  Server, Bot, Sparkles, Zap, Settings,
} from 'lucide-react';

type Provider = 'meta' | 'evolution' | 'botpress' | 'twilio';

interface WaConfig {
  configured: boolean;
  activo: boolean;
  provider: Provider;
  // Meta
  phoneNumberId?: string;
  wabaId?: string;
  webhookVerifyToken?: string;
  hasAccessToken?: boolean;
  // Evolution
  apiUrl?: string;
  hasApiKey?: boolean;
  instancia?: string;
  // Botpress
  botpressUrl?: string;
  botpressBotId?: string;
  // Twilio
  twilioAccountSid?: string;
  hasTwilioAuthToken?: boolean;
  twilioFromNumber?: string;
  twilioMessagingServiceSid?: string;
  // Común
  nombreNegocio?: string;
  mensajeBienvenida?: string;
  autoConfirmar?: boolean;
  recordatorioHoras?: number;
  funcionesBot?: { reservas?: boolean; delivery?: boolean; menu?: boolean; faq?: boolean };
  faqItems?: Array<{ pregunta: string; respuesta: string; palabrasClave?: string[] }>;
}

const PROVIDERS: Array<{ key: Provider; nombre: string; emoji: string; desc: string; pros: string; contras: string }> = [
  {
    key: 'evolution',
    nombre: 'Evolution API',
    emoji: '🆓',
    desc: 'Auto-hosteado en tu servidor. Conectás tu WhatsApp escaneando un QR.',
    pros: 'Gratis · Sin verificación Meta · Funciona con tu número normal',
    contras: 'Necesitás mantenerlo · WhatsApp puede bloquear si abusás',
  },
  {
    key: 'meta',
    nombre: 'Meta Cloud API',
    emoji: '🏢',
    desc: 'WhatsApp Business API oficial de Meta. Requiere verificación del negocio.',
    pros: 'Oficial y estable · 1000 conversaciones/mes gratis',
    contras: 'Proceso de verificación largo · Necesitás un número dedicado',
  },
  {
    key: 'twilio',
    nombre: 'Twilio',
    emoji: '☎️',
    desc: 'WhatsApp Business API vía Twilio. Sin verificación de Meta, pago por uso.',
    pros: 'Setup en 15 min · API estable · No requiere Business Verification',
    contras: 'Pago por mensaje (~$0.005/c.u.) · ~$10-30/mes según volumen',
  },
  {
    key: 'botpress',
    nombre: 'Botpress',
    emoji: '🤖',
    desc: 'Plataforma de bot conversacional avanzada (Cloud o self-hosted).',
    pros: 'Bot muy potente con flows visuales · IA integrada',
    contras: 'Requiere conector WhatsApp adicional (360Dialog/Meta) · Soporte en CRM en desarrollo',
  },
];

export function WhatsAppConfigPage() {
  const [cfg, setCfg] = useState<WaConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; name?: string; error?: string } | null>(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendResult, setSendResult] = useState<{ ok: boolean; error?: string } | null>(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [provider, setProvider] = useState<Provider>('evolution');

  // Meta fields
  const [accessToken, setAccessToken] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [showToken, setShowToken] = useState(false);

  // Evolution fields
  const [apiUrl, setApiUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [instancia, setInstancia] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  // Botpress fields
  const [botpressUrl, setBotpressUrl] = useState('');
  const [botpressBotId, setBotpressBotId] = useState('');

  // Twilio fields
  const [twilioAccountSid, setTwilioAccountSid] = useState('');
  const [twilioAuthToken, setTwilioAuthToken] = useState('');
  const [twilioFromNumber, setTwilioFromNumber] = useState('');
  const [twilioMessagingServiceSid, setTwilioMessagingServiceSid] = useState('');
  const [showTwilioToken, setShowTwilioToken] = useState(false);

  // Común
  const [activo, setActivo] = useState(false);
  const [nombreNegocio, setNombreNegocio] = useState('');
  const [mensajeBienvenida, setMensajeBienvenida] = useState('');
  const [autoConfirmar, setAutoConfirmar] = useState(true);
  const [recordatorioHoras, setRecordatorioHoras] = useState(24);
  const [funciones, setFunciones] = useState({ reservas: true, delivery: false, menu: false, faq: false });
  const [faqItems, setFaqItems] = useState<Array<{ pregunta: string; respuesta: string; palabrasClave?: string[] }>>([]);

  const [testPhone, setTestPhone] = useState('');

  useEffect(() => { loadConfig(); }, []);

  async function loadConfig() {
    try {
      const [data, wh] = await Promise.all([
        whatsappApi.getConfig() as Promise<WaConfig>,
        whatsappApi.webhookUrl(),
      ]);
      setCfg(data);
      setWebhookUrl(wh.url);
      setProvider(data.provider ?? 'evolution');
      setActivo(!!data.activo);
      setNombreNegocio(data.nombreNegocio ?? '');
      setMensajeBienvenida(data.mensajeBienvenida ?? '');
      setAutoConfirmar(data.autoConfirmar ?? true);
      setRecordatorioHoras(data.recordatorioHoras ?? 24);
      setFunciones({ reservas: true, delivery: false, menu: false, faq: false, ...(data.funcionesBot ?? {}) });
      setFaqItems(data.faqItems ?? []);
      // Meta
      setPhoneNumberId(data.phoneNumberId ?? '');
      setWabaId(data.wabaId ?? '');
      setVerifyToken(data.webhookVerifyToken ?? '');
      // Evolution
      setApiUrl(data.apiUrl ?? '');
      setInstancia(data.instancia ?? '');
      // Botpress
      setBotpressUrl(data.botpressUrl ?? '');
      setBotpressBotId(data.botpressBotId ?? '');
      // Twilio
      setTwilioAccountSid(data.twilioAccountSid ?? '');
      setTwilioFromNumber(data.twilioFromNumber ?? '');
      setTwilioMessagingServiceSid(data.twilioMessagingServiceSid ?? '');
    } catch (e: any) {
      alert(`Error cargando config: ${e.message}`);
    } finally { setLoading(false); }
  }

  async function guardar() {
    setSaving(true);
    try {
      const body: any = {
        provider,
        activo,
        nombreNegocio,
        mensajeBienvenida,
        autoConfirmar,
        recordatorioHoras,
        funcionesBot: funciones,
        faqItems,
      };
      if (provider === 'meta') {
        if (accessToken) body.accessToken = accessToken;
        body.phoneNumberId = phoneNumberId;
        body.wabaId = wabaId;
        body.webhookVerifyToken = verifyToken;
      } else if (provider === 'evolution') {
        body.apiUrl = apiUrl;
        if (apiKey) body.apiKey = apiKey;
        body.instancia = instancia;
      } else if (provider === 'botpress') {
        body.botpressUrl = botpressUrl;
        body.botpressBotId = botpressBotId;
      } else if (provider === 'twilio') {
        body.twilioAccountSid = twilioAccountSid;
        if (twilioAuthToken) body.twilioAuthToken = twilioAuthToken;
        body.twilioFromNumber = twilioFromNumber;
        body.twilioMessagingServiceSid = twilioMessagingServiceSid;
      }
      await whatsappApi.saveConfig(body);
      await loadConfig();
      setAccessToken('');
      setApiKey('');
      setTwilioAuthToken('');
      alert('✅ Configuración guardada');
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    } finally { setSaving(false); }
  }

  async function testear() {
    setTesting(true); setTestResult(null);
    try {
      const r = await whatsappApi.test();
      setTestResult(r);
    } catch (e: any) {
      setTestResult({ ok: false, error: e.message });
    } finally { setTesting(false); }
  }

  async function enviarTest() {
    if (!testPhone) return;
    setSendingTest(true); setSendResult(null);
    try {
      const r = await whatsappApi.sendTest(testPhone);
      setSendResult(r);
    } catch (e: any) {
      setSendResult({ ok: false, error: e.message });
    } finally { setSendingTest(false); }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="size-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5">
      <header>
        <h1 className="font-display text-3xl font-bold text-ink-900 flex items-center gap-3">
          <MessageCircle className="size-7 text-emerald-500" />
          WhatsApp Business
        </h1>
        <p className="text-sm text-ink-500 mt-1">
          Configurá el proveedor de WhatsApp y las funciones del bot que verán tus clientes.
        </p>
      </header>

      {/* SELECTOR DE PROVEEDOR */}
      <section className="card p-4 sm:p-5">
        <h2 className="font-bold text-ink-800 text-lg mb-3 flex items-center gap-2">
          <Server className="size-5 text-indigo-500" /> Proveedor
        </h2>
        <div className="grid sm:grid-cols-3 gap-3">
          {PROVIDERS.map((p) => {
            const activo = provider === p.key;
            return (
              <button
                key={p.key}
                onClick={() => setProvider(p.key)}
                className={`text-left p-3 rounded-xl border-2 transition ${
                  activo ? "border-emerald-500 bg-emerald-50 shadow-sm" : "border-ink-200 hover:border-ink-300"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">{p.emoji}</span>
                  <span className="font-bold text-ink-900 text-sm">{p.nombre}</span>
                  {activo && <CheckCircle2 className="size-4 text-emerald-600 ml-auto" />}
                </div>
                <p className="text-xs text-ink-600 leading-relaxed">{p.desc}</p>
                <p className="text-[10px] text-emerald-700 mt-2">✓ {p.pros}</p>
                <p className="text-[10px] text-rose-700">⚠ {p.contras}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* CONFIGURACIÓN ESPECÍFICA DEL PROVIDER */}
      <section className="card p-4 sm:p-5">
        <h2 className="font-bold text-ink-800 text-lg mb-3 flex items-center gap-2">
          <Settings className="size-5 text-indigo-500" /> Credenciales {PROVIDERS.find((p) => p.key === provider)?.nombre}
        </h2>

        {provider === 'meta' && (
          <div className="space-y-3">
            <Field label="Access Token de WhatsApp Business" hint="Generado en Meta Business Settings → System users">
              <div className="flex gap-2">
                <input type={showToken ? "text" : "password"} value={accessToken} onChange={(e) => setAccessToken(e.target.value)}
                  placeholder={cfg?.hasAccessToken ? "(configurado — dejá vacío para no cambiar)" : "EAAxxxxxxx..."}
                  className="input flex-1 font-mono text-xs" />
                <button onClick={() => setShowToken(!showToken)} className="px-3 py-2 rounded-lg border border-ink-300 hover:bg-ink-50">
                  {showToken ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Phone Number ID" hint="Sale en tu cuenta de Meta">
                <input value={phoneNumberId} onChange={(e) => setPhoneNumberId(e.target.value)}
                  placeholder="123456789012345" className="input" />
              </Field>
              <Field label="WABA ID (opcional)">
                <input value={wabaId} onChange={(e) => setWabaId(e.target.value)} className="input" />
              </Field>
            </div>
            <Field label="Webhook Verify Token (lo elegís vos)">
              <input value={verifyToken} onChange={(e) => setVerifyToken(e.target.value)}
                placeholder="cualquier_cadena_secreta" className="input" />
            </Field>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
              <p className="font-semibold mb-1">📡 URL del Webhook (configurá esto en Meta):</p>
              <code className="bg-white px-2 py-1 rounded break-all">{webhookUrl}</code>
            </div>
          </div>
        )}

        {provider === 'evolution' && (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900">
              <p className="font-semibold mb-1">✅ Conexión automática</p>
              <p>No hace falta configurar nada técnico: tocá <b>Conectar / Mostrar QR</b> abajo y escaneá el código con tu WhatsApp. El servidor, la clave y la instancia se configuran solos.</p>
            </div>

            <ConexionEvolution />

            <details className="group">
              <summary className="cursor-pointer text-xs text-ink-500 select-none">Configuración avanzada (opcional)</summary>
              <div className="space-y-3 mt-3">
                <Field label="URL del servidor Evolution" hint="Se completa sola; dejala vacía salvo que uses un servidor propio">
                  <input value={apiUrl} onChange={(e) => setApiUrl(e.target.value)}
                    placeholder="http://evolution-api:8080" className="input font-mono text-xs" />
                </Field>
                <Field label="API Key">
                  <div className="flex gap-2">
                    <input type={showApiKey ? "text" : "password"} value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                      placeholder={cfg?.hasApiKey ? "(configurada — dejá vacío para no cambiar)" : "(se usa la del servidor)"}
                      className="input flex-1 font-mono text-xs" />
                    <button onClick={() => setShowApiKey(!showApiKey)} className="px-3 py-2 rounded-lg border border-ink-300 hover:bg-ink-50">
                      {showApiKey ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                  </div>
                </Field>
                <Field label="Nombre de la instancia" hint="Se genera solo (crm-org-<id>) si lo dejás vacío">
                  <input value={instancia} onChange={(e) => setInstancia(e.target.value)}
                    placeholder="(automático)" className="input" />
                </Field>
              </div>
            </details>
          </div>
        )}

        {provider === 'botpress' && (
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
              <p className="font-semibold mb-1">🚧 Integración Botpress en desarrollo</p>
              <p>Por ahora podés configurar los datos pero el bot aún no procesa mensajes vía Botpress directamente. Mantené Evolution o Meta activos para producción.</p>
            </div>
            <Field label="URL de tu Botpress" hint="Cloud o self-hosted">
              <input value={botpressUrl} onChange={(e) => setBotpressUrl(e.target.value)}
                placeholder="https://app.botpress.cloud" className="input" />
            </Field>
            <Field label="Bot ID">
              <input value={botpressBotId} onChange={(e) => setBotpressBotId(e.target.value)} className="input" />
            </Field>
          </div>
        )}

        {provider === 'twilio' && (
          <div className="space-y-3">
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-xs text-cyan-900">
              <p className="font-semibold mb-1">☎️ Cómo obtener tus credenciales Twilio</p>
              <ol className="list-decimal ml-5 space-y-1 mt-1">
                <li>Creá cuenta en <a href="https://www.twilio.com/try-twilio" target="_blank" className="text-cyan-700 underline">twilio.com/try-twilio</a> (gratis, $15 USD de crédito de prueba)</li>
                <li>En el dashboard verás el <b>Account SID</b> y <b>Auth Token</b></li>
                <li>Activá el <b>WhatsApp Sandbox</b> (Messaging → Try WhatsApp) — para probar inmediatamente</li>
                <li>El "From" del Sandbox es <code className="bg-white px-1 rounded">+14155238886</code></li>
                <li>Para producción: aplicá por tu propio número WhatsApp (~1-3 días aprobación)</li>
              </ol>
            </div>
            <Field label="Account SID" hint="Empieza con AC...">
              <input value={twilioAccountSid} onChange={(e) => setTwilioAccountSid(e.target.value)}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" className="input font-mono text-xs" />
            </Field>
            <Field label="Auth Token">
              <div className="flex gap-2">
                <input type={showTwilioToken ? "text" : "password"} value={twilioAuthToken}
                  onChange={(e) => setTwilioAuthToken(e.target.value)}
                  placeholder={cfg?.hasTwilioAuthToken ? "(configurado — dejá vacío para no cambiar)" : "tu auth token"}
                  className="input flex-1 font-mono text-xs" />
                <button onClick={() => setShowTwilioToken(!showTwilioToken)}
                  className="px-3 py-2 rounded-lg border border-ink-300 hover:bg-ink-50">
                  {showTwilioToken ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </Field>
            <Field label="Número WhatsApp (From)" hint="Con código país y +, ej: +14155238886 (sandbox) o tu número aprobado">
              <input value={twilioFromNumber} onChange={(e) => setTwilioFromNumber(e.target.value)}
                placeholder="+14155238886" className="input font-mono" />
            </Field>
            <Field label="Messaging Service SID (opcional)" hint="Si usás Messaging Service en vez del From directo">
              <input value={twilioMessagingServiceSid} onChange={(e) => setTwilioMessagingServiceSid(e.target.value)}
                placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" className="input font-mono text-xs" />
            </Field>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
              <p className="font-semibold">⚠️ Para probar con WhatsApp Sandbox</p>
              <p>Antes de poder mandar mensajes desde tu número, vos (y cualquier número de prueba) tenés que enviar un WhatsApp con el código de unión (algo como <code className="bg-white px-1 rounded">join word-word</code>) al número +1 415 523 8886. El código exacto lo ves en tu consola Twilio.</p>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mt-4">
          <button onClick={testear} disabled={testing}
            className="px-4 py-2 rounded-lg bg-indigo-100 text-indigo-700 hover:bg-indigo-200 disabled:opacity-50 text-sm font-semibold inline-flex items-center gap-2">
            {testing && <Loader2 className="size-4 animate-spin" />}
            Probar conexión
          </button>
          {testResult && (
            <span className={`text-sm font-semibold inline-flex items-center gap-1 ${testResult.ok ? "text-emerald-700" : "text-rose-700"}`}>
              {testResult.ok ? <CheckCircle2 className="size-4" /> : <XCircle className="size-4" />}
              {testResult.ok ? testResult.name : testResult.error}
            </span>
          )}
        </div>
      </section>

      {/* FUNCIONES DEL BOT */}
      <section className="card p-4 sm:p-5">
        <h2 className="font-bold text-ink-800 text-lg mb-1 flex items-center gap-2">
          <Bot className="size-5 text-indigo-500" /> Funciones del bot
        </h2>
        <p className="text-xs text-ink-500 mb-3">Activá qué puede hacer el bot cuando un cliente le escribe.</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {[
            { key: "reservas" as const, emoji: "📅", titulo: "Reservas", desc: "Reservar, ver y cancelar reservas." },
            { key: "delivery" as const, emoji: "🛵", titulo: "Delivery", desc: "Pedido guiado: categoría → producto → dirección." },
            { key: "menu"     as const, emoji: "📖", titulo: "Ver menú", desc: "Cliente puede consultar la carta." },
            { key: "faq"      as const, emoji: "ℹ️", titulo: "FAQ", desc: "Responder preguntas frecuentes." },
          ].map((f) => (
            <label key={f.key}
              className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${
                funciones[f.key] ? "border-emerald-500 bg-emerald-50" : "border-ink-200 hover:border-ink-300"
              }`}>
              <input type="checkbox" checked={!!funciones[f.key]}
                onChange={(e) => setFunciones({ ...funciones, [f.key]: e.target.checked })}
                className="size-4 mt-0.5 accent-emerald-600" />
              <div>
                <p className="font-semibold text-ink-800 text-sm">{f.emoji} {f.titulo}</p>
                <p className="text-xs text-ink-500">{f.desc}</p>
              </div>
            </label>
          ))}
        </div>

        {/* FAQ Editor */}
        {funciones.faq && (
          <div className="mt-4 border-t border-ink-100 pt-4">
            <h3 className="font-semibold text-sm text-ink-700 mb-2">FAQ ({faqItems.length})</h3>
            <div className="space-y-2 mb-2">
              {faqItems.map((it, i) => (
                <div key={i} className="border border-ink-200 rounded-lg p-2 bg-ink-50/50">
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <input value={it.pregunta}
                      onChange={(e) => setFaqItems(faqItems.map((x, j) => j === i ? { ...x, pregunta: e.target.value } : x))}
                      placeholder="¿Pregunta? (ej: ¿Cuáles son sus horarios?)"
                      className="col-span-5 text-xs px-2 py-1 border border-ink-200 rounded" />
                    <input value={(it.palabrasClave ?? []).join(', ')}
                      onChange={(e) => setFaqItems(faqItems.map((x, j) => j === i ? { ...x, palabrasClave: e.target.value.split(',').map(s => s.trim()).filter(Boolean) } : x))}
                      placeholder="palabras clave (horario, abren)"
                      className="col-span-3 text-xs px-2 py-1 border border-ink-200 rounded" />
                    <textarea value={it.respuesta}
                      onChange={(e) => setFaqItems(faqItems.map((x, j) => j === i ? { ...x, respuesta: e.target.value } : x))}
                      placeholder="Respuesta"
                      rows={1}
                      className="col-span-3 text-xs px-2 py-1 border border-ink-200 rounded resize-none" />
                    <button onClick={() => setFaqItems(faqItems.filter((_, j) => j !== i))}
                      className="col-span-1 text-rose-600 text-xs">✕</button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setFaqItems([...faqItems, { pregunta: '', respuesta: '', palabrasClave: [] }])}
              className="text-xs text-indigo-600 hover:underline">+ Agregar FAQ</button>
          </div>
        )}
      </section>

      {/* GENERAL */}
      <section className="card p-4 sm:p-5">
        <h2 className="font-bold text-ink-800 text-lg mb-3 flex items-center gap-2">
          <Sparkles className="size-5 text-indigo-500" /> Personalización del bot
        </h2>
        <div className="space-y-3">
          <Field label="Nombre del negocio (como lo verá el cliente)">
            <input value={nombreNegocio} onChange={(e) => setNombreNegocio(e.target.value)}
              placeholder="ej: Robusta Café" className="input" />
          </Field>
          <Field label="Mensaje de bienvenida">
            <textarea value={mensajeBienvenida} onChange={(e) => setMensajeBienvenida(e.target.value)}
              rows={2} placeholder="¡Hola! 👋 Bienvenido a ..." className="input resize-none" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 p-3 rounded-lg border border-ink-200 cursor-pointer">
              <input type="checkbox" checked={autoConfirmar} onChange={(e) => setAutoConfirmar(e.target.checked)}
                className="size-4 accent-emerald-600" />
              <span className="text-sm">Auto-confirmar reservas (sin pedir respuesta del cliente)</span>
            </label>
            <Field label="Recordatorio horas antes">
              <input type="number" value={recordatorioHoras} onChange={(e) => setRecordatorioHoras(Number(e.target.value))}
                min={0} max={72} className="input" />
            </Field>
          </div>
          <label className="flex items-center gap-2 pt-2">
            <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)}
              className="size-5 accent-emerald-600" />
            <span className="font-semibold text-ink-800">Activar WhatsApp</span>
          </label>
        </div>
      </section>

      {/* PROBAR ENVÍO */}
      <section className="card p-4 sm:p-5">
        <h2 className="font-bold text-ink-800 text-lg mb-3 flex items-center gap-2">
          <Send className="size-5 text-indigo-500" /> Probar envío
        </h2>
        <div className="flex gap-2">
          <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)}
            placeholder="+584121234567" className="input flex-1" />
          <button onClick={enviarTest} disabled={!testPhone || sendingTest}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2">
            {sendingTest && <Loader2 className="size-4 animate-spin" />}
            <Zap className="size-4" /> Enviar test
          </button>
        </div>
        {sendResult && (
          <p className={`text-sm mt-2 ${sendResult.ok ? "text-emerald-700" : "text-rose-700"}`}>
            {sendResult.ok ? "✓ Mensaje enviado!" : `✗ ${sendResult.error}`}
          </p>
        )}
      </section>

      {/* GUARDAR */}
      <div className="sticky bottom-4 z-30">
        <button onClick={guardar} disabled={saving}
          className="w-full px-6 py-3 bg-indigo-600 text-white rounded-xl shadow-lg hover:bg-indigo-700 disabled:opacity-50 font-bold text-base inline-flex items-center justify-center gap-2">
          {saving && <Loader2 className="size-5 animate-spin" />}
          Guardar configuración
        </button>
      </div>
    </div>
  );
}

/** Conexión de WhatsApp por Evolution: QR + estado en vivo, sin entrar al manager. */
function ConexionEvolution() {
  const [estado, setEstado] = useState<{ state?: string; connected?: boolean; error?: string } | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const refrescar = async () => {
    try {
      const r = await whatsappApi.instanciaEstado();
      setEstado(r);
      if (r?.connected) setQr(null);
    } catch (e: any) { setEstado({ error: e.message }); }
  };

  useEffect(() => {
    refrescar();
    const id = setInterval(refrescar, 5000);
    return () => clearInterval(id);
  }, []);

  const conectar = async () => {
    setCargando(true); setMsg(null);
    try {
      const r = await whatsappApi.instanciaConectar();
      if (!r?.ok) { setMsg(r?.error || 'No se pudo conectar.'); return; }
      setEstado({ state: r.state, connected: r.connected });
      if (r.connected) { setQr(null); setMsg('✅ WhatsApp ya está conectado.'); }
      else if (r.qr) { setQr(String(r.qr).startsWith('data:') ? r.qr : `data:image/png;base64,${r.qr}`); setMsg('Escaneá el QR con WhatsApp → Dispositivos vinculados.'); }
      else { setMsg('No se recibió QR. Guardá la config (URL, API Key, instancia) y reintentá.'); }
    } catch (e: any) { setMsg(e.message); }
    finally { setCargando(false); }
  };

  const desconectar = async () => {
    if (!window.confirm('¿Cerrar la sesión de WhatsApp de este número?')) return;
    setCargando(true); setMsg(null);
    try { await whatsappApi.instanciaLogout(); setQr(null); await refrescar(); setMsg('Sesión cerrada.'); }
    catch (e: any) { setMsg(e.message); }
    finally { setCargando(false); }
  };

  const connected = !!estado?.connected;
  const state = estado?.state;

  return (
    <div className="border border-ink-200 rounded-xl p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <p className="font-semibold text-sm text-ink-800">Conexión de WhatsApp</p>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
          connected ? 'bg-emerald-100 text-emerald-700'
            : state === 'connecting' ? 'bg-amber-100 text-amber-700'
            : 'bg-ink-100 text-ink-500'}`}>
          {connected ? 'Conectado' : state === 'connecting' ? 'Conectando…' : 'Desconectado'}
        </span>
      </div>

      {estado?.error && <p className="text-xs text-rose-600 mb-2">{estado.error}</p>}

      {!connected
        ? <button onClick={conectar} disabled={cargando} className="btn-primary text-sm px-4">
            {cargando ? 'Generando…' : qr ? 'Regenerar QR' : 'Conectar / Mostrar QR'}
          </button>
        : <button onClick={desconectar} disabled={cargando} className="btn-secondary text-sm px-4 text-red-600">
            Cerrar sesión
          </button>}

      {qr && !connected && (
        <div className="mt-3 flex flex-col items-center gap-2">
          <img src={qr} alt="QR de WhatsApp" className="w-56 h-56 border border-ink-200 rounded-lg bg-white" />
          <p className="text-xs text-ink-500 text-center">WhatsApp → Ajustes → Dispositivos vinculados → Vincular dispositivo. El estado se actualiza solo.</p>
        </div>
      )}

      {msg && <p className="text-xs text-ink-600 mt-2">{msg}</p>}

      <p className="text-[11px] text-ink-400 mt-3">Guardá primero la configuración (arriba); luego conectá acá — ya no hace falta entrar al manager de Evolution.</p>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-ink-600 uppercase tracking-wide mb-1">
        {label}
      </label>
      {children}
      {hint && <p className="text-[11px] text-ink-400 mt-0.5">{hint}</p>}
    </div>
  );
}

